<template>
  <Loader
    v-if="isLoading || isConnecting"
    style="z-index:1102"
  />
  <WhatsappOnboardingWizard
    v-if="!isWhatsappAccountConnected"
    :shop-id="shopId"
    :connect-loading="isConnecting"
    @connect="handleLinkWhatsappAccount"
  />
  <div v-else>
    <div class="whatsapp-account-card">
      <div class="whatsapp-account-card__content">
        <div class="whatsapp-account-card__identity">
          <div class="waba__icon">
            <i class="fa-solid fa-building-user" />
          </div>
          <div class="waba__details">
            <span class="waba__name">{{ getDisplayValue(wabaBusinessDetails.verified_name) }}</span>
            <span class="waba__phone">{{ getDisplayValue(wabaBusinessDetails.display_phone_number) }}</span>
            <span class="waba__description">{{ t('storeParameters.services.whatsappBusinessParameter.connected.description') }}</span>
            <div
              v-if="isDetailsLoading"
              class="waba__loading"
            >
              {{ t('storeParameters.services.whatsappBusinessParameter.connected.loading') }}
            </div>
            <div
              v-else
              class="waba__details-sections"
            >
              <div class="waba__section">
                <h4 class="waba__section-title">
                  {{ t('storeParameters.services.whatsappBusinessParameter.connected.sections.identifiers') }}
                </h4>
                <div class="waba__meta-grid waba__meta-grid--compact">
                  <div class="waba__meta-item">
                    <span class="waba__meta-label">{{ t('storeParameters.services.whatsappBusinessParameter.connected.fields.wabaId') }}</span>
                    <span class="waba__meta-value">{{ getDisplayValue(wabaBusinessDetails.waba_id) }}</span>
                  </div>
                  <div class="waba__meta-item">
                    <span class="waba__meta-label">{{ t('storeParameters.services.whatsappBusinessParameter.connected.fields.numberId') }}</span>
                    <span class="waba__meta-value">{{ getDisplayValue(wabaBusinessDetails.number_id) }}</span>
                  </div>
                </div>
              </div>

              <div class="waba__section">
                <h4 class="waba__section-title">
                  {{ t('storeParameters.services.whatsappBusinessParameter.connected.sections.health') }}
                </h4>
                <div class="waba__meta-grid">
                  <div class="waba__meta-item waba__meta-item--wide">
                    <span class="waba__meta-label">{{ t('storeParameters.services.whatsappBusinessParameter.connected.fields.wabaAccountStatus') }}</span>
                    <span
                      class="waba__badge"
                      :class="getWabaAccountStatusBadgeClass(wabaBusinessDetails.account_status)"
                    >
                      {{ getWabaAccountStatusLabel(wabaBusinessDetails.account_status) }}
                    </span>
                    <small class="waba__meta-help">
                      {{ t('storeParameters.services.whatsappBusinessParameter.connected.wabaAccountStatusHelp') }}
                    </small>
                  </div>
                  <div class="waba__meta-item">
                    <span class="waba__meta-label">{{ t('storeParameters.services.whatsappBusinessParameter.connected.fields.codeVerificationStatus') }}</span>
                    <span
                      class="waba__badge"
                      :class="getCodeVerificationBadgeClass(wabaBusinessDetails.code_verification_status)"
                    >
                      {{ getCodeVerificationLabel(wabaBusinessDetails.code_verification_status) }}
                    </span>
                <small class="waba__meta-help">
                  {{ t('storeParameters.services.whatsappBusinessParameter.connected.verificationStatusHelp') }}
                </small>
                  </div>
                  <div class="waba__meta-item">
                    <span class="waba__meta-label">{{ t('storeParameters.services.whatsappBusinessParameter.connected.fields.messagingLimitTier') }}</span>
                    <span
                      class="waba__badge"
                      :class="getTierBadgeClass(wabaBusinessDetails.messaging_limit_tier)"
                    >
                      {{ getTierLabel(wabaBusinessDetails.messaging_limit_tier) }}
                    </span>
                    <small class="waba__meta-help">
                      {{ t('storeParameters.services.whatsappBusinessParameter.connected.messagingTierHelp') }}
                    </small>
                  </div>
                  <div class="waba__meta-item waba__meta-item--wide">
                    <span class="waba__meta-label">{{ t('storeParameters.services.whatsappBusinessParameter.connected.fields.qualityScore') }}</span>
                    <span
                      class="waba__badge"
                      :class="getQualityBadgeClass(wabaBusinessDetails.quality_score?.score)"
                    >
                      {{ getQualityLabel(wabaBusinessDetails.quality_score?.score) }}
                    </span>
                    <small class="waba__meta-help">
                      {{ t('storeParameters.services.whatsappBusinessParameter.connected.qualityScoreHelp') }}
                    </small>
                  </div>
                  <div class="waba__meta-item waba__meta-item--wide">
                    <span class="waba__meta-label">{{ t('storeParameters.services.whatsappBusinessParameter.connected.fields.businessStatus') }}</span>
                    <span
                      class="waba__badge"
                      :class="getBusinessStatusBadgeClass(wabaBusinessDetails.business_verification_status)"
                    >
                      {{ getBusinessStatusLabel(wabaBusinessDetails.business_verification_status) }}
                    </span>
                    <small class="waba__meta-help">
                      {{ t('storeParameters.services.whatsappBusinessParameter.connected.businessStatusHelp') }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
            <small
              v-if="wabaBusinessDetails.live_unavailable"
              class="waba__fallback"
            >
              {{ t('storeParameters.services.whatsappBusinessParameter.connected.liveUnavailable') }}
            </small>
          </div>
        </div>
      </div>
      <div class="whatsapp-account-card__actions">
        <div class="waba__unlink">
          <Button
            :label="t('storeParameters.services.whatsappBusinessParameter.fields.unlink')"
            class="p-button p-button-danger p-button-outlined"
            icon="fa-solid fa-unlink"
            type="button"
            :loading="isUnlinking"
            :aria-label="t('storeParameters.services.whatsappBusinessParameter.fields.unlink')"
            @click="handleUnlinkWhatsappAccount"
          />
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="globalSave"
    class="flex align-content-center justify-content-center align-items-center mt-5"
  >
    <slot name="save" />
  </div>

  <Dialog
    v-model:visible="isUnlinkConfirmVisible"
    :modal="true"
    class="unlink-confirm-dialog"
    :style="{ width: 'min(42rem, 92vw)' }"
    :header="t('storeParameters.services.whatsappBusinessParameter.connected.unlinkConfirm.title')"
  >
    <div class="unlink-confirm">
      <div class="unlink-confirm__lead">
        <span
          class="unlink-confirm__icon"
          aria-hidden="true"
        >
          <i class="fa-solid fa-triangle-exclamation" />
        </span>
        <p class="unlink-confirm__message">
          {{ t('storeParameters.services.whatsappBusinessParameter.connected.unlinkConfirm.message') }}
        </p>
      </div>

      <div
        v-if="isUnlinkImpactLoading"
        class="unlink-confirm__impact-card unlink-confirm__impact-card--loading"
      >
        <i
          class="fa-solid fa-spinner fa-spin unlink-confirm__spinner"
          aria-hidden="true"
        />
        <span>{{ t('storeParameters.services.whatsappBusinessParameter.connected.unlinkConfirm.impactLoading') }}</span>
      </div>

      <div
        v-else-if="unlinkImpact"
        class="unlink-confirm__impact-card"
      >
        <p class="unlink-confirm__impact-title">
          {{ t('storeParameters.services.whatsappBusinessParameter.connected.unlinkConfirm.impactTitle') }}
        </p>
        <ul class="unlink-confirm__impact-list">
          <li>
            {{ t('storeParameters.services.whatsappBusinessParameter.connected.unlinkConfirm.impactTemplates', unlinkImpact.templatesToReset, { count: unlinkImpact.templatesToReset }) }}
          </li>
          <li>
            {{ t('storeParameters.services.whatsappBusinessParameter.connected.unlinkConfirm.impactAutomation', unlinkImpact.automationStepsToDisable, { count: unlinkImpact.automationStepsToDisable }) }}
          </li>
          <li>
            {{ t('storeParameters.services.whatsappBusinessParameter.connected.unlinkConfirm.impactBulk', unlinkImpact.bulkCampaignsToDraft, { count: unlinkImpact.bulkCampaignsToDraft }) }}
          </li>
        </ul>
      </div>

      <p class="unlink-confirm__reconnect-hint">
        {{ t('storeParameters.services.whatsappBusinessParameter.connected.unlinkConfirm.reconnectHint') }}
      </p>
    </div>
    <template #footer>
      <Button
        :label="t('storeParameters.services.whatsappBusinessParameter.connected.unlinkConfirm.cancel')"
        class="p-button p-button-secondary"
        type="button"
        :disabled="isUnlinking"
        @click="isUnlinkConfirmVisible = false"
      />
      <Button
        :label="t('storeParameters.services.whatsappBusinessParameter.connected.unlinkConfirm.confirm')"
        class="p-button p-button-danger"
        type="button"
        :loading="isUnlinking"
        @click="confirmUnlinkWhatsappAccount"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent,
  defineExpose,
  reactive,
  ref,
  Ref,
  onMounted,
} from 'vue';

import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Loader from '@/components/layout/Loader.vue';

import { saveShopParamsOnRedis, fetchShopConfigurationsNest, saveShopConfigurationsNest } from '@/composables/shop/ShopsConfiguration';
import useVuelidate from '@vuelidate/core';
import { ErrorConfigForm } from '@/types/automated-scenarios';
import { ShopsConfigurationInputItem } from '@/types/generated-types/graphql';
import { showToastError, showToastSuccess } from '@/helpers';

import { serialize } from 'php-serialize';

import { useI18n } from 'vue-i18n';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { useFacebookLogin } from '@/composables/facebook/useFacebookLogin';
import WhatsappOnboardingWizard from '@/components/whatsapp-onboarding/WhatsappOnboardingWizard.vue';

export default defineComponent({
  name: 'WhatsappBusinessServiceParameter',

  components: {
    Loader,
    Button,
    Dialog,
    WhatsappOnboardingWizard,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },

    globalSave: {
      type: Boolean,
      required: false,
      default: false,
    },

    name: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['whatsapp-account-connected'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const isLoading = ref(false);
    const isConnecting = ref(false);

    const isUnlinking = ref(false);
    const isUnlinkConfirmVisible = ref(false);
    const isUnlinkImpactLoading = ref(false);
    const unlinkImpact = ref<{
      templatesToReset: number;
      automationStepsToDisable: number;
      bulkCampaignsToDraft: number;
    } | null>(null);
    const isDetailsLoading = ref(false);

    const isWhatsappAccountConnected = ref(false);

    const { loginAndConnectWABA } = useFacebookLogin(
      props.shopId,
      process.env.VUE_APP_FACEBOOK_WHATSAPP_APP_ID || '685038527618616',
      process.env.VUE_APP_FACEBOOK_WHATSAPP_GRAPH_VERSION || 'v25.0',
    );

    const componentFieldErrorsKey = ref(0);
    const error = ref();

    const field = reactive<Record<string, any>>({
      shopRequiredData_whatsapp_business: {},
    });
    const wabaBusinessDetails = reactive<Record<string, any>>({
      verified_name: '',
      code_verification_status: '',
      waba_id: '',
      display_phone_number: '',
      number_id: '',
      messaging_limit_tier: '',
      quality_score: {
        score: '',
      },
      business_verification_status: '',
      account_status: '',
      live_unavailable: true,
    });

    const validate = async (): Promise<ErrorConfigForm> => {
      const rules: Record<string, any> = {
        shopRequiredData_whatsapp_business: {},
      };

      const v$ = useVuelidate(rules, field);
      const success = await v$.value.$validate();

      return {
        success,
        validate: v$,
      };
    };

    const fetchWhatsappInformations = async (options?: { manageLoading?: boolean }) => {
      const manageLoading = options?.manageLoading !== false;
      try {
        if (manageLoading) {
          isLoading.value = true;
        }
        field.shopRequiredData_whatsapp_business = {};

        // Fetch configurations from Nest REST API
        const keys = Object.keys(field);
        const data = await fetchShopConfigurationsNest(props.shopId, keys);

        if (data) {
          // Map API response to field
          Object.keys(data).forEach((key) => {
            if (key in field) {
              field[key] = data[key];
            }
          });
        }

        const whatsappBusiness = field.shopRequiredData_whatsapp_business;

        if (whatsappBusiness) {
          const parsedWhatsappBusiness =
            typeof whatsappBusiness === 'string'
              ? JSON.parse(whatsappBusiness)
              : whatsappBusiness;
          isWhatsappAccountConnected.value = Object.keys(parsedWhatsappBusiness).length > 0;
          field.shopRequiredData_whatsapp_business = parsedWhatsappBusiness;
          if (isWhatsappAccountConnected.value) {
            await fetchWhatsappBusinessDetails();
          }
          // Emit event to parent with current connection status
          emit('whatsapp-account-connected', isWhatsappAccountConnected.value);
        } else {
          isWhatsappAccountConnected.value = false;
        }
      } catch (err) {
      } finally {
        if (manageLoading) {
          isLoading.value = false;
        }
      }
    };

    const hydrateFallbackDetailsFromConfig = () => {
      const config = field.shopRequiredData_whatsapp_business || {};
      wabaBusinessDetails.verified_name = config.business_name || '';
      wabaBusinessDetails.code_verification_status = '';
      wabaBusinessDetails.waba_id = config.waba_id || '';
      wabaBusinessDetails.display_phone_number = config.phone_number || '';
      wabaBusinessDetails.number_id = config.phone_number_id || '';
      wabaBusinessDetails.messaging_limit_tier = '';
      wabaBusinessDetails.quality_score = { score: '' };
      wabaBusinessDetails.business_verification_status = String(
        config.business_verification_status || '',
      ).toUpperCase();
      wabaBusinessDetails.account_status = String(
        config.account_status || '',
      ).toUpperCase();
      wabaBusinessDetails.live_unavailable = true;
    };

    const fetchWhatsappBusinessDetails = async () => {
      isDetailsLoading.value = true;
      hydrateFallbackDetailsFromConfig();
      try {
        const response = await nestCall(
          'get',
          NestApiType.MESSAGING,
          '/whatsapp/business-account-details',
          {},
          '',
          undefined,
          true,
          { shop_id: Number(props.shopId) },
        );

        const details = response?.data || null;
        if (!details) {
          return;
        }

        wabaBusinessDetails.verified_name = details.verified_name || wabaBusinessDetails.verified_name;
        wabaBusinessDetails.code_verification_status = details.code_verification_status || '';
        wabaBusinessDetails.waba_id = details.waba_id || wabaBusinessDetails.waba_id;
        wabaBusinessDetails.display_phone_number = details.display_phone_number || wabaBusinessDetails.display_phone_number;
        wabaBusinessDetails.number_id = details.number_id || wabaBusinessDetails.number_id;
        wabaBusinessDetails.messaging_limit_tier = details.messaging_limit_tier || '';
        wabaBusinessDetails.quality_score = {
          score: details?.quality_score?.score || '',
        };
        wabaBusinessDetails.business_verification_status = String(
          details.business_verification_status || '',
        ).toUpperCase();
        wabaBusinessDetails.account_status = String(
          details.account_status || '',
        ).toUpperCase();
        wabaBusinessDetails.live_unavailable = !!details.live_unavailable;
      } catch (err) {
        hydrateFallbackDetailsFromConfig();
      } finally {
        isDetailsLoading.value = false;
      }
    };

    const getDisplayValue = (value: string | null | undefined): string => (
      value && String(value).trim() !== ''
        ? String(value)
        : t('storeParameters.services.whatsappBusinessParameter.connected.valueUnavailable')
    );

    const getCodeVerificationLabel = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (normalized === 'VERIFIED') return t('storeParameters.services.whatsappBusinessParameter.connected.verificationStatus.verified');
      if (normalized === 'NOT_VERIFIED') return t('storeParameters.services.whatsappBusinessParameter.connected.verificationStatus.notVerified');
      if (normalized === 'EXPIRED') return t('storeParameters.services.whatsappBusinessParameter.connected.verificationStatus.expired');
      return t('storeParameters.services.whatsappBusinessParameter.connected.verificationStatus.unknown');
    };

    const getCodeVerificationBadgeClass = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (normalized === 'VERIFIED') return 'waba__badge--success';
      if (normalized === 'NOT_VERIFIED') return 'waba__badge--warning';
      if (normalized === 'EXPIRED') return 'waba__badge--danger';
      return 'waba__badge--neutral';
    };

    const getTierLabel = (tier: string): string => {
      const normalized = String(tier || '').toUpperCase();
      if (!normalized) return '-';
      return t(`storeParameters.services.whatsappBusinessParameter.connected.messagingTiers.${normalized}`);
    };

    const getTierBadgeClass = (tier: string): string => {
      const normalized = String(tier || '').toUpperCase();
      if (normalized === 'TIER_100K' || normalized === 'TIER_UNLIMITED') return 'waba__badge--success';
      if (normalized === 'TIER_10K' || normalized === 'TIER_1K') return 'waba__badge--info';
      if (normalized === 'TIER_250' || normalized === 'TIER_50') return 'waba__badge--warning';
      return 'waba__badge--neutral';
    };

    const getQualityLabel = (score: string): string => {
      const normalized = String(score || '').toUpperCase();
      if (normalized === 'GREEN') return t('storeParameters.services.whatsappBusinessParameter.connected.qualityScores.green');
      if (normalized === 'YELLOW') return t('storeParameters.services.whatsappBusinessParameter.connected.qualityScores.yellow');
      if (normalized === 'RED') return t('storeParameters.services.whatsappBusinessParameter.connected.qualityScores.red');
      return t('storeParameters.services.whatsappBusinessParameter.connected.qualityScores.unknown');
    };

    const getQualityBadgeClass = (score: string): string => {
      const normalized = String(score || '').toUpperCase();
      if (normalized === 'GREEN') return 'waba__badge--success';
      if (normalized === 'YELLOW') return 'waba__badge--warning';
      if (normalized === 'RED') return 'waba__badge--danger';
      return 'waba__badge--neutral';
    };

    const getWabaAccountStatusLabel = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (!normalized) {
        return t('storeParameters.services.whatsappBusinessParameter.connected.wabaAccountStatuses.unknown');
      }
      return t(
        `storeParameters.services.whatsappBusinessParameter.connected.wabaAccountStatuses.${normalized}`,
        t('storeParameters.services.whatsappBusinessParameter.connected.wabaAccountStatuses.unknown'),
      );
    };

    const getWabaAccountStatusBadgeClass = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (normalized === 'ACTIVE' || normalized === 'CONNECTED') return 'waba__badge--success';
      if (
        normalized === 'BANNED'
        || normalized === 'RESTRICTED'
        || normalized === 'DELETED'
        || normalized === 'FLAGGED'
      ) return 'waba__badge--danger';
      if (
        normalized === 'INELIGIBLE'
        || normalized === 'INELIGIBLE_ON_BEHALF_OF_WABA'
        || normalized === 'PENDING'
        || normalized === 'UNVERIFIED'
        || normalized === 'NOT_STARTED'
        || normalized === 'RATE_LIMITED'
        || normalized === 'OFFLINE'
        || normalized === 'DISCONNECTED'
        || normalized === 'MIGRATED'
      ) return 'waba__badge--warning';
      return 'waba__badge--neutral';
    };

    const getBusinessStatusLabel = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (!normalized) return t('storeParameters.services.whatsappBusinessParameter.connected.businessStatuses.unknown');
      return t(
        `storeParameters.services.whatsappBusinessParameter.connected.businessStatuses.${normalized}`,
        t('storeParameters.services.whatsappBusinessParameter.connected.businessStatuses.unknown'),
      );
    };

    const getBusinessStatusBadgeClass = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (normalized === 'VERIFIED') return 'waba__badge--success';
      if (
        normalized === 'REJECTED'
        || normalized === 'REVOKED'
        || normalized === 'FAILED'
        || normalized === 'EXPIRED'
      ) return 'waba__badge--danger';
      if (
        normalized === 'PENDING'
        || normalized === 'PENDING_NEED_MORE_INFO'
        || normalized === 'PENDING_SUBMISSION'
      ) return 'waba__badge--warning';
      return 'waba__badge--neutral';
    };

    // eslint-disable-next-line consistent-return
    const handleSave = async () => {
      const validation = await validate();
      if (!validation.success) {
        error.value = validation.validate;
        componentFieldErrorsKey.value += 1;
        return {
          err: validation.validate.value.$errors.length,
          configs: [],
        };
      }

      // Prepare configurations for Nest API
      // WhatsApp data is saved as a single JSON string, not split by lang
      const configurationsToSave: Array<{ key: string; value: any; lang: string | null }> = [];

      Object.entries(field).forEach(([key, value]) => {
        configurationsToSave.push({
          key,
          value: typeof value === 'object' ? JSON.stringify(value) : serialize(value),
          lang: null,
        });
      });

      if (props.globalSave) {
        // For global save, return GraphQL format for compatibility
        const shopsConfigArray: ShopsConfigurationInputItem[] = configurationsToSave.map((config) => ({
          key: config.key,
          value: config.value,
          lang: config.lang || '',
        }));
        return {
          err: null,
          configs: shopsConfigArray,
        };
      }

      if (configurationsToSave.length) {
        try {
          const result = await saveShopConfigurationsNest(props.shopId, configurationsToSave);
          if (result) {
            await saveShopParamsOnRedis(props.shopId);
          } else {
            showToastError('GENERIC_ERROR');
          }
        } catch (err) {
          showToastError('GENERIC_ERROR');
        }
      }
    };

    const handleLinkWhatsappAccount = async () => {
      try {
        await loginAndConnectWABA(async () => {
          await fetchWhatsappInformations({ manageLoading: false });
          if (!props.globalSave) {
            await handleSave();
          }
        }, {
          onBackendWorkStart: () => {
            isConnecting.value = true;
          },
        });
      } catch (err) {
        // Errors are toasted in useFacebookLogin
      } finally {
        isConnecting.value = false;
      }
    };

    const fetchUnlinkImpact = async () => {
      isUnlinkImpactLoading.value = true;
      unlinkImpact.value = null;

      try {
        const response = await nestCall(
          'get',
          NestApiType.MESSAGING,
          '/whatsapp/unlink-impact',
          {},
          '',
          undefined,
          true,
          { shop_id: Number(props.shopId) },
        );

        if (response?.data) {
          unlinkImpact.value = response.data;
        }
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        isUnlinkImpactLoading.value = false;
      }
    };

    const handleUnlinkWhatsappAccount = async () => {
      isUnlinkConfirmVisible.value = true;
      await fetchUnlinkImpact();
    };

    const confirmUnlinkWhatsappAccount = async () => {
      isUnlinking.value = true;
      try {
        const response = await nestCall('post', NestApiType.MESSAGING, '/whatsapp/unlink-whatsapp-account', {}, '', {
          shop_id: Number(props.shopId),
        });
        if (response?.error) {
          showToastError('GENERIC_ERROR');
          return;
        }
        showToastSuccess('savedSuccessful');
        isUnlinkConfirmVisible.value = false;
        await fetchWhatsappInformations();
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        isUnlinking.value = false;
      }
    };

    defineExpose({
      handleSave,
    });

    onMounted(async () => {
      await fetchWhatsappInformations();
    });

    return {
      t,
      field,
      error,
      componentFieldErrorsKey,
      isLoading,
      isConnecting,
      isUnlinking,
      handleLinkWhatsappAccount,
      handleUnlinkWhatsappAccount,
      confirmUnlinkWhatsappAccount,
      isWhatsappAccountConnected,
      handleSave,
      isUnlinkConfirmVisible,
      isUnlinkImpactLoading,
      unlinkImpact,
      isDetailsLoading,
      wabaBusinessDetails,
      getDisplayValue,
      getCodeVerificationLabel,
      getCodeVerificationBadgeClass,
      getTierLabel,
      getTierBadgeClass,
      getQualityLabel,
      getQualityBadgeClass,
      getBusinessStatusLabel,
      getBusinessStatusBadgeClass,
      getWabaAccountStatusLabel,
      getWabaAccountStatusBadgeClass,
    };
  },
});
</script>

<style scoped lang="scss">
.whatsapp-account-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 1.5rem;
  padding: 1.25rem 1.5rem;
  background-color: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 0.875rem;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.9rem;
    min-width: 0;
  }

  &__identity {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
  }

  .waba__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background-color: #e2e8f0;
    flex-shrink: 0;
    border: 1px solid var(--surface-border);

    i {
      font-size: 1.5rem;
      color: #334155;
    }
  }

  .waba__details {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;

    .waba__name {
      font-size: 1rem;
      font-weight: 600;
      color: var(--text-color);
    }

    .waba__phone {
      font-size: 0.875rem;
      color: var(--text-color-secondary);
    }

    .waba__description {
      font-size: 0.75rem;
      color: var(--text-color-secondary);
      font-style: italic;
      margin-top: 0.2rem;
      max-width: 44rem;
    }

    .waba__loading {
      margin-top: 0.2rem;
      font-size: 0.8125rem;
      color: var(--text-color-secondary);
    }

    .waba__details-sections {
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      margin-top: 0.1rem;
    }

    .waba__section {
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
    }

    .waba__section-title {
      margin: 0;
      font-size: 0.8rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--text-color-secondary);
    }

    .waba__meta-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(240px, 1fr));
      gap: 0.9rem 1rem;
      margin-top: 0.15rem;
      width: 100%;
    }

    .waba__meta-grid--compact {
      grid-template-columns: repeat(2, minmax(220px, 1fr));
    }

    .waba__meta-item {
      display: flex;
      flex-direction: column;
      gap: 0.3rem;
      min-width: 0;
      padding: 0.7rem 0.75rem;
      border: 1px solid var(--surface-border);
      border-radius: 0.65rem;
      background-color: color-mix(in srgb, var(--surface-card) 95%, #ffffff 5%);
    }

    .waba__meta-item--wide {
      grid-column: 1 / -1;
    }

    .waba__meta-label {
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--text-color-secondary);
      letter-spacing: 0.01em;
    }

    .waba__meta-value {
      font-size: 1.05rem;
      color: var(--text-color);
      word-break: break-word;
      font-weight: 700;
    }

    .waba__meta-help {
      font-size: 0.75rem;
      color: var(--text-color-secondary);
      line-height: 1.35;
      max-width: 34rem;
    }

    .waba__fallback {
      margin-top: 0.1rem;
      font-size: 0.75rem;
      color: var(--text-color-secondary);
    }

    .waba__badge {
      display: inline-flex;
      align-items: center;
      border-radius: 999px;
      padding: 0.18rem 0.62rem;
      font-size: 0.75rem;
      font-weight: 700;
      width: fit-content;
    }

    .waba__badge--success {
      color: #166534;
      background: #dcfce7;
    }

    .waba__badge--warning {
      color: #854d0e;
      background: #fef9c3;
    }

    .waba__badge--danger {
      color: #991b1b;
      background: #fee2e2;
    }

    .waba__badge--info {
      color: #1d4ed8;
      background: #dbeafe;
    }

    .waba__badge--neutral {
      color: #334155;
      background: #e2e8f0;
    }
  }

  .waba__unlink {
    flex-shrink: 0;
    align-self: flex-start;
    justify-self: end;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;

    .waba__details .waba__meta-grid {
      grid-template-columns: 1fr;
    }

    .waba__details .waba__meta-grid--compact {
      grid-template-columns: 1fr;
    }

    &__actions {
      align-items: flex-start;
    }

    .waba__unlink {
      justify-self: start;
      margin-top: 0.15rem;
    }
  }
}

:deep(.unlink-confirm-dialog) {
  .p-dialog-header {
    padding: 1.25rem 1.5rem 0.75rem;
  }

  .p-dialog-content {
    padding: 0 1.5rem 1.25rem;
    text-align: left;
  }

  .p-dialog-footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.625rem;
    padding: 0.75rem 1.5rem 1.25rem;
    border-top: 1px solid var(--surface-border, #e2e8f0);
  }
}

.unlink-confirm {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
}

.unlink-confirm__lead {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}

.unlink-confirm__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
  font-size: 1rem;
}

.unlink-confirm__message {
  margin: 0.2rem 0 0;
  color: var(--text-color, #0f172a);
  font-size: 0.9375rem;
  line-height: 1.55;
}

.unlink-confirm__impact-card {
  padding: 0.875rem 1rem 0.95rem;
  border: 1px solid var(--surface-border, #e2e8f0);
  border-radius: 0.625rem;
  background: var(--surface-50, #f8fafc);

  &--loading {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    color: var(--text-color-secondary, #64748b);
    font-size: 0.875rem;
  }
}

.unlink-confirm__spinner {
  color: var(--primary-color, #3b82f6);
}

.unlink-confirm__impact-title {
  margin: 0 0 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-color-secondary, #64748b);
}

.unlink-confirm__impact-list {
  margin: 0;
  padding: 0 0 0 1.125rem;
  list-style: disc;
  list-style-position: outside;

  li {
    margin-bottom: 0.45rem;
    padding-left: 0.15rem;
    font-size: 0.9375rem;
    line-height: 1.5;
    color: var(--text-color, #0f172a);
    text-align: left;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.unlink-confirm__reconnect-hint {
  margin: 0;
  padding: 0.75rem 0.875rem;
  border-left: 3px solid var(--primary-color, #3b82f6);
  border-radius: 0 0.375rem 0.375rem 0;
  background: rgba(59, 130, 246, 0.06);
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--text-color-secondary, #475569);
}
</style>
